'use strict';

const { getDependencyClass } = require('../../lib/dependencies.js');
const performanceMeasure = require('../../lib/performanceMeasure.js');
let measure = performanceMeasure('webcomponents:breadcrumbs');

getDependencyClass('phoenix-page').then((pageClass) => {
    const componentTag = 'phoenix-breadcrumbs';
    const containerTag = componentTag + '-container';
    const itemTag = componentTag + '-item';
    const toggleEventName = 'phoenix:breadcrumbs:toggle';
    const collapsedClass = 'is-collapsed';

    /**
     * PhoenixBreadcrumbs is a Web Component that manages a list of
     * navigational "breadcrumbs", particularly when they exceed the
     * viewport that contains them. The component expects markup
     * in the form of the markup shown below. It's important that
     * these elements are styled so that the `phoenix-breadcrumbs-item`
     * items are shown horizontally, and that the parent element
     * is set to overflow with a scrollbar (which can be hidden)
     * so that the component can determine if the items exceed their
     * container or not.
     *
     * @example
     *     <phoenix-breadcrumbs id="unique-id">
     *         <phoenix-notifier
     *             event-name="phoenix:breadcrumbs:toggle"
     *             event-value="unique-id">
     *             <button></button>
     *         </phoenix-notifier>
     *         <phoenix-breadcrumbs-container>
     *             <ul>
     *                 <li phoenix-breadcrumbs-item>Item 1</li>
     *                 <li phoenix-breadcrumbs-item>Item 2</li>
     *             </ul>
     *         </phoenix-breadcrumbs-container>
     *     </phoenix-breadcrumbs>
     */
    class PhoenixBreadcrumbs extends pageClass.PhoenixElement {
        connectedCallback() {
            super.connectedCallback();
            this._windowWidth = 0;
            this._collapsed = false;
            this._collapsingEnabled = true;

            let containerPromise = this.waitForElement(containerTag, { untilDomReady: true });
            let buttonPromise = this.waitForElement('phoenix-notifier > button', { untilDomReady: true });

            Promise.all([containerPromise, buttonPromise]).then((els) => {
                this.container = els[0];
                this.button = els[1];
                // Breadcrumbs are represented as 'li' tags that have
                // a 'phoenix-breadcrumbs-item' attribute
                this.breadcrumbs = els[0].querySelectorAll('[' + itemTag + ']');
                this._updateCollapsedState();

                // we don't want to run this on every resize call
                this.resizeListener = pageClass.util.debounce(this._updateCollapsedState.bind(this), 250);
                window.addEventListener('resize', this.resizeListener);
            });
        }

        disconnectedCallback() {
            super.disconnectedCallback();
            this._unlisten();
        }

        get observedEmittedEvents() {
            return [toggleEventName];
        }

        emittedEventCallback(name, event) {
            const eventValue = event.detail && event.detail.value;

            if (name === toggleEventName && eventValue === this.getAttribute('id')) {
                if (this._collapsingEnabled) {
                    this.collapsed = !this.collapsed;
                }
            }
        }

        _unlisten() {
            if (this.resizeListener) {
                window.removeEventListener('resize', this.resizeListener);
            }
        }

        _updateCollapsedState() {
            let newWindowWidth = window.innerWidth;
            if (newWindowWidth !== this._windowWidth) {
                this._windowWidth = newWindowWidth;
                this.collapsed = this._collapsingEnabled =
                    this.container.scrollWidth > this.container.offsetWidth;
                if (this._collapsingEnabled) {
                    this.button.classList.remove('is-hidden');
                } else {
                    this.button.classList.add('is-hidden');
                }
            }
        }

        _resetHiddenItems() {
            this.breadcrumbs.forEach((item) => item.classList.remove('is-truncated', 'is-hidden'));
        }

        _hideSelectedItems() {
            let container = this.container;
            let adjustedContainerWidth = container.scrollWidth;
            let numberOfBreadcrumbs = this.breadcrumbs.length;

            this.breadcrumbs.forEach((item, index) => {
                let containerWidth = container.offsetWidth,
                    itemWidth = item.offsetWidth;
                if (index !== numberOfBreadcrumbs - 1 && containerWidth < adjustedContainerWidth) {
                    item.classList.add('is-hidden');
                    adjustedContainerWidth -= parseInt(itemWidth, 10);
                } else if (index === numberOfBreadcrumbs - 1 && containerWidth < adjustedContainerWidth) {
                    item.classList.add('is-truncated');
                }
            });
        }

        set collapsed(collapsed) {
            if (collapsed !== this._collapsed) {
                this._collapsed = collapsed;
                let newLabel;
                if (collapsed) {
                    newLabel = 'Expand';
                    this.classList.add(collapsedClass);
                } else {
                    newLabel = 'Collapse';
                    this.classList.remove(collapsedClass);
                }
                this.button.setAttribute('aria-label', newLabel + ' Breadcrumbs');
            }
            // Regardless of whether the collapsed state actually changed,
            // make sure the items are updated to be consistent with this state.
            if (collapsed) {
                this._hideSelectedItems();
            } else {
                this._resetHiddenItems();
            }
        }

        get collapsed() {
            return this._collapsed;
        }
    }

    class PhoenixBreadcrumbsContainer extends HTMLElement {}

    window.customElements.define(containerTag, PhoenixBreadcrumbsContainer);
    window.customElements.define(componentTag, PhoenixBreadcrumbs);
    measure.end();
});
